// cspell:ignore oaic
import type { ReactNode } from "react";
import { Link } from "wouter";

/*
 * The privacy policy (/privacy) and terms of use (/terms): public, no sign-in, linked from the
 * sign-in page, the account menu and Settings › ServiceM8. ServiceM8's Add-on Store needs the
 * privacy policy's address, and its platform policy needs it shown in the app. Keep them true to
 * what the app does: what's synced (servicem8/datasets.ts), deleting a business's data
 * (companyData.ts), backups and logs (DEPLOY.md).
 */

/** Who runs the app. */
const OPERATOR = {
    name: "Matthew McCormick",
    abn: "50 697 945 854",
    /** Whose law governs the terms. */
    jurisdiction: "the Australian Capital Territory",
    email: "support@outbackapps.com",
};
const UPDATED = "14 October 2026";

function LegalPage({ title, children }: Readonly<{ title: string; children: ReactNode }>) {
    return (
        <div className="min-h-full bg-canvas px-4 py-10">
            <article className="mx-auto max-w-2xl space-y-6 text-ink [&_h2]:mt-8 [&_h2]:mb-2 [&_h2]:text-lg [&_li]:mt-1 [&_p]:m-0 [&_ul]:m-0 [&_ul]:list-disc [&_ul]:pl-5">
                <header className="space-y-1">
                    <div className="text-sm font-semibold text-brand-ink">Outback Workbench</div>
                    <h1 className="m-0 text-2xl">{title}</h1>
                    <p className="text-sm text-muted">Last updated {UPDATED}</p>
                </header>
                {children}
                <footer className="flex flex-wrap gap-4 border-t border-line pt-4 text-sm">
                    <Link href="/privacy" className="text-brand-ink">
                        Privacy policy
                    </Link>
                    <Link href="/terms" className="text-brand-ink">
                        Terms of use
                    </Link>
                    <a href="/" className="text-brand-ink">
                        Open Outback Workbench
                    </a>
                </footer>
            </article>
        </div>
    );
}

const Email = () => <a href={`mailto:${OPERATOR.email}`}>{OPERATOR.email}</a>;

export function Privacy() {
    return (
        <LegalPage title="Privacy policy">
            <p>
                Outback Workbench is a ServiceM8 add-on run by {OPERATOR.name} (ABN {OPERATOR.abn}), trading as Outback Apps, in Australia
                (&ldquo;we&rdquo;). This policy explains what information the add-on uses, why, where it&apos;s kept, and how to have it
                deleted. It applies to the add-on and to workbench.outbackapps.com. We handle personal information in line with the Australian
                Privacy Principles.
            </p>

            <h2>Information from ServiceM8</h2>
            <p>When a business connects its ServiceM8 account, the add-on keeps a copy of the ServiceM8 data its tools use:</p>
            <ul>
                <li>business details (name, account ID, time zone and the account holder&apos;s email);</li>
                <li>staff (names, email addresses and security roles) and the security roles themselves;</li>
                <li>tax rates, materials, bundles and suppliers;</li>
                <li>
                    clients, client contacts and jobs only if a manager switches them on in Settings, or the details of a particular job or client
                    when someone links it to something in a tool.
                </li>
            </ul>
            <p>
                The add-on only asks ServiceM8 for the permissions these need. It keeps the copy up to date, and changes made in the add-on are
                written back to ServiceM8 under the name of the staff member who made them, so ServiceM8&apos;s own permissions and history
                apply.
            </p>

            <h2>Information we collect</h2>
            <ul>
                <li>
                    <strong>Signing in:</strong> your email address, and your name from ServiceM8. Sign-in codes are emailed through your
                    business&apos;s ServiceM8 account; they&apos;re stored only in scrambled (hashed) form and expire after 10 minutes.
                </li>
                <li>
                    <strong>Your work in the add-on:</strong> what you and your colleagues enter, such as pricing settings, supplier prices and
                    their history, and settings for who can do what.
                </li>
                <li>
                    <strong>Preferences:</strong> your colour theme, saved to your account.
                </li>
                <li>
                    <strong>Security and diagnostics:</strong> your browser type for each signed-in session; your IP address, briefly, to limit
                    repeated sign-in attempts; and server logs of requests and errors (including errors from your browser, with your email
                    address so we can help).
                </li>
            </ul>
            <p>
                We use one cookie, to keep you signed in. Inside ServiceM8, where that cookie can&apos;t be used, your browser keeps the
                sign-in for that tab instead. Your browser also remembers a few display choices on your device, such as window sizes. There
                are no advertising or tracking cookies, and no analytics. ServiceM8 handles billing for the add-on, so we never receive payment
                details.
            </p>

            <h2>How we use it</h2>
            <p>
                Only to run the add-on for your business: to show and update your data, sign you in, keep the add-on secure, fix problems and
                answer your questions. We don&apos;t sell information, use it for advertising, or share it with anyone for their own purposes.
            </p>

            <h2>Who else handles it</h2>
            <ul>
                <li>
                    <strong>Amazon Web Services</strong> hosts the add-on and its backups, in Sydney, Australia.
                </li>
                <li>
                    <strong>ServiceM8</strong>, where your data comes from and where changes are written back.
                </li>
                <li>
                    <strong>Google Fonts</strong> supplies the add-on&apos;s typeface, so your browser contacts Google when it loads (Google
                    receives your IP address, not your data).
                </li>
            </ul>
            <p>We&apos;ll only disclose information otherwise if the law requires it.</p>

            <h2>Security</h2>
            <p>
                Everything travels over encrypted connections (HTTPS). The key that lets the add-on reach your ServiceM8 account is stored
                encrypted, and backups are encrypted. Each business sees only its own data, and what each person can do follows their
                ServiceM8 security role.
            </p>

            <h2>How long we keep it, and deleting it</h2>
            <ul>
                <li>We keep a business&apos;s data while it uses the add-on.</li>
                <li>
                    A manager can delete everything kept for their business at any time, in Settings › ServiceM8 › &ldquo;Delete this
                    business&apos;s data&rdquo;. It&apos;s removed at once, along with the accounts of people who only used the add-on for that
                    business.
                </li>
                <li>
                    Anyone can ask us to delete their information, or all of their business&apos;s, by emailing <Email />. We&apos;ll do it and
                    confirm within 30 days.
                </li>
                <li>Backups and server logs are kept for up to 30 days, so deleted information is gone from them within 30 days too.</li>
                <li>Removing the add-on in ServiceM8 stops it reaching your account. To have your data deleted too, use the option above or email us.</li>
            </ul>

            <h2>Your rights</h2>
            <p>
                You can ask to see the information we hold about you, or to correct it (most of it comes from ServiceM8, so correcting it there
                also corrects it here). If you&apos;re unhappy with how we&apos;ve handled your information, email us first; if we don&apos;t
                resolve it, you can complain to the Office of the Australian Information Commissioner (oaic.gov.au).
            </p>

            <h2>Your business&apos;s part</h2>
            <p>
                The business that connects ServiceM8 decides which of its staff can use the add-on (through ServiceM8 security roles) and is
                responsible for its own obligations to its staff and clients. We handle the data on its behalf, only to run the add-on.
            </p>

            <h2>Changes and contact</h2>
            <p>
                If we change this policy, we&apos;ll update the date above, and let the businesses using the add-on know of any significant change.
                Questions: <Email />.
            </p>
        </LegalPage>
    );
}

export function Terms() {
    return (
        <LegalPage title="Terms of use">
            <p>
                These terms cover your use of Outback Workbench, a ServiceM8 add-on run by {OPERATOR.name} (ABN {OPERATOR.abn}), trading as
                Outback Apps (&ldquo;we&rdquo;). By connecting it to a ServiceM8 account or signing in, you agree to them on behalf of yourself
                and your business.
            </p>

            <h2>The add-on</h2>
            <ul>
                <li>It&apos;s made by Outback Apps, not by ServiceM8, and ServiceM8 doesn&apos;t support it. Your use of ServiceM8 itself stays under ServiceM8&apos;s terms.</li>
                <li>We may improve or change it over time, and will try to give notice of changes that affect how you work.</li>
            </ul>

            <h2>Fees</h2>
            <ul>
                <li>
                    The price is shown in the add-on&apos;s listing in the ServiceM8 Add-on Store. ServiceM8 bills it with your ServiceM8
                    subscription, under ServiceM8&apos;s billing terms, and we never see or handle your payment details.
                </li>
                <li>We&apos;ll give at least 30 days&apos; notice before raising the price.</li>
                <li>To stop paying, remove the add-on in ServiceM8.</li>
            </ul>

            <h2>Your data</h2>
            <ul>
                <li>Your business&apos;s data stays yours. We use it only to run the add-on, as our privacy policy explains.</li>
                <li>You can delete it at any time (Settings › ServiceM8) or ask us to.</li>
                <li>
                    Changes you make in the add-on, such as prices, are written to your ServiceM8 account. You&apos;re responsible for checking
                    them before you rely on them, for example before quoting or invoicing.
                </li>
            </ul>

            <h2>Using it properly</h2>
            <p>
                Use the add-on only for your business&apos;s own work, and don&apos;t try to reach other businesses&apos; data, get around
                its security or overload it. We may suspend access that puts the add-on or other users at risk.
            </p>

            <h2>Availability and responsibility</h2>
            <p>
                We work to keep the add-on running and your data safe, with backups, but we can&apos;t promise it will always be available or
                free of errors. To the extent the law allows, we aren&apos;t liable for indirect losses (such as lost profits) from using it, and
                our total liability is limited to resupplying the service. Nothing in these terms limits rights you have under the Australian
                Consumer Law that can&apos;t be excluded.
            </p>

            <h2>Ending</h2>
            <p>
                You can stop at any time by removing the add-on in ServiceM8, which also ends its charges, and delete your data as above. If we
                ever close the add-on, we&apos;ll give at least 30 days&apos; notice, stop charging, and then delete everything we hold.
            </p>

            <h2>Law and contact</h2>
            <p>
                These terms are governed by the law of {OPERATOR.jurisdiction}, Australia. If we change them, we&apos;ll update the date above. Questions
                or support: <Email />.
            </p>
        </LegalPage>
    );
}
